import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { ParkedLocationField, ParkedLocationDialog } from "../components/ParkedLocationPicker";
import { FuelLevelField, FuelLevelDialog } from "../components/FuelLevelField";
import FuelReceipts from "@/components/FuelReceipts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Car,
  LogIn,
  LogOut,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Search,
  RefreshCw,
  Users,
  Edit,
  Download,
  Fuel,
  Timer,
  Lock,
  Unlock as LockOpen,
  Ban,
  X,
  Trash2,
  Pencil,
  ClipboardList,
  EyeOff,
  Eye,
  Plus,
  MessageSquare,
  CheckSquare,
  Square,
  Send,
  Satellite,
  BatteryMedium,
  Filter,
  Bell,
  Receipt
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, formatDistanceToNow } from "date-fns";

export default function PatrolAdmin({ embedded = false }) {
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  // Geotab's own fuel readings, keyed by car_id. Purely supplementary — shown
  // beside the manual level, never in place of it, and empty whenever Geotab
  // is unconfigured, unreachable or the car isn't linked to a device.
  const [geotabFuel, setGeotabFuel] = useState({});
  const [geotabVoltage, setGeotabVoltage] = useState({});
  const [units, setUnits] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("cars");
  // Unpaid fuel receipts, for the Receipts tab's count (FuelReceipts reports it).
  const [receiptsUnpaid, setReceiptsUnpaid] = useState(0);
  useEffect(() => {
    axios.get(`${API}/patrol/fuel-receipts`, { params: { status: "unpaid" }, withCredentials: true })
      .then((r) => setReceiptsUnpaid(r.data?.unpaid_count || 0))
      .catch(() => {});
  }, []);
  // Vehicle filter — same as the Patrol tab's (car_ids to show, empty = all),
  // remembered per device under its own key so the two tabs filter separately.
  const [carFilter, setCarFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fsdCarFilter") || "[]"); } catch { return []; }
  });
  const saveCarFilter = (ids) => {
    setCarFilter(ids);
    try { localStorage.setItem("fsdCarFilter", JSON.stringify(ids)); } catch { /* private mode */ }
  };
  const toggleCarFilter = (carId) =>
    saveCarFilter(carFilter.includes(carId) ? carFilter.filter((id) => id !== carId) : [...carFilter, carId]);
  // Department-wide switches: patrol events by push and/or WhatsApp.
  const [notifSettings, setNotifSettings] = useState(null);
  useEffect(() => {
    axios.get(`${API}/patrol/notification-settings`, { withCredentials: true })
      .then((r) => setNotifSettings(r.data))
      .catch(() => setNotifSettings(null));
  }, []);
  const setNotifSwitch = async (key, value) => {
    const prev = notifSettings;
    setNotifSettings({ ...(prev || {}), [key]: value });
    try {
      const r = await axios.put(`${API}/patrol/notification-settings`, { [key]: value }, { withCredentials: true });
      setNotifSettings(r.data);
      toast.success(`Patrol ${key === "push_enabled" ? "push" : "WhatsApp"} notifications ${value ? "on" : "off"}`);
    } catch (e) {
      setNotifSettings(prev);
      toast.error(e.response?.data?.detail || "Couldn't save");
    }
  };
  
  // Dialog states
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showOutOfServiceDialog, setShowOutOfServiceDialog] = useState(false);
  const [showEditLocationDialog, setShowEditLocationDialog] = useState(false);
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  const [showCooldownDialog, setShowCooldownDialog] = useState(false);
  const [showHideConfirmDialog, setShowHideConfirmDialog] = useState(false);
  const [showDroneUnlockDialog, setShowDroneUnlockDialog] = useState(false);
  const [droneUnlockCar, setDroneUnlockCar] = useState(null);
  const [unlockingCarId, setUnlockingCarId] = useState(null);
  const [lockingCarId, setLockingCarId] = useState(null);
  const [droneLockCar, setDroneLockCar] = useState(null); // confirm dialog open for this car
  const [hideAction, setHideAction] = useState(null); // { car, hide: true/false }
  
  // Form states
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedUnitsToLogin, setSelectedUnitsToLogin] = useState([]);
  const [unitSearchQuery, setUnitSearchQuery] = useState("");
  const [parkedLocation, setParkedLocation] = useState("");
  // How the parked location was chosen — see the same field on the Patrol tab.
  const [parkedLocationMeta, setParkedLocationMeta] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showFuelPicker, setShowFuelPicker] = useState(false);
  const [logoutNote, setLogoutNote] = useState("");
  const [outOfServiceNote, setOutOfServiceNote] = useState("");
  const [editParkedLocation, setEditParkedLocation] = useState("");
  // The edit dialog's own location picker, and where its pick came from —
  // separate from the logout one so opening either can't clobber the other.
  const [showEditLocationPicker, setShowEditLocationPicker] = useState(false);
  const [editParkedLocationMeta, setEditParkedLocationMeta] = useState(null);
  const [editFuelLevel, setEditFuelLevel] = useState("");
  const [showEditFuelDialog, setShowEditFuelDialog] = useState(false);
  // The edit dialog's own level picker — separate from the logout one so
  // opening either can't clobber the other's pending level.
  const [showEditFuelPicker, setShowEditFuelPicker] = useState(false);
  const [logoutFuelLevel, setLogoutFuelLevel] = useState("");

  // Reserve & Cooldown states
  const [reserveDuration, setReserveDuration] = useState(15);
  const [reserveForUnit, setReserveForUnit] = useState("");
  const [cooldownUnit, setCooldownUnit] = useState("");
  const [cooldownDuration, setCooldownDuration] = useState(15);
  const [cooldownCarId, setCooldownCarId] = useState("__all__");
  const [activeCooldowns, setActiveCooldowns] = useState([]);

  // Session management states
  const [showEditSessionDialog, setShowEditSessionDialog] = useState(false);
  const [showDeleteSessionDialog, setShowDeleteSessionDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [editSessionData, setEditSessionData] = useState({ unit_name: "", car_id: "", car_name: "", session_start: "", timestamp: "" });

  // Todo states
  const [todos, setTodos] = useState([]);
  const [newTodoText, setNewTodoText] = useState("");
  const [expandedTodo, setExpandedTodo] = useState(null);
  const [newUpdateText, setNewUpdateText] = useState("");
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editingTodoText, setEditingTodoText] = useState("");

  const FUEL_LEVELS = ["Full", "7/8", "3/4", "5/8", "1/2", "3/8", "1/4", "1/8", "Empty"];

  // The vehicle's own fuel reading, sitting next to the manual badge. Same
  // color thresholds as the Fleet tab; a satellite icon rather than the fuel
  // pump so the two are never mistaken for each other. Renders nothing at all
  // when there's no reading, so cars without a Geotab device look unchanged.
  const geotabFuelBadge = (carId) => {
    const reading = geotabFuel[carId];
    if (!reading || typeof reading.percent !== "number") return null;
    const tone =
      reading.percent <= 12.5 ? "border-red-500 text-red-600 dark:text-red-400" :
      reading.percent <= 37.5 ? "border-amber-500 text-amber-600 dark:text-amber-400" :
      "border-green-500 text-green-600 dark:text-green-400";
    const reportedAgo = (() => {
      try {
        return reading.reported_at
          ? ` · reported ${formatDistanceToNow(new Date(reading.reported_at), { addSuffix: true })}`
          : "";
      } catch { return ""; }
    })();
    return (
      <Badge
        variant="outline"
        className={`text-xs ${tone}`}
        title={`From the vehicle (Geotab)${reportedAgo}`}
      >
        <Satellite className="w-3 h-3 mr-1" />
        {reading.as_eighths ? `${reading.as_eighths} · ` : ""}{Math.round(reading.percent)}%
      </Badge>
    );
  };
  // Battery voltage from Geotab. 12-volt system: under 12.0 is a weak/dead
  // battery, 13.2+ means the alternator is charging (engine running).
  const geotabVoltageBadge = (carId) => {
    const reading = geotabVoltage[carId];
    if (!reading || typeof reading.volts !== "number") return null;
    const v = reading.volts;
    const tone =
      v < 12.0 ? "border-red-500 text-red-600 dark:text-red-400" :
      v < 12.4 ? "border-amber-500 text-amber-600 dark:text-amber-400" :
      v >= 13.2 ? "border-emerald-500 text-emerald-600 dark:text-emerald-400" :
      "border-sky-500 text-sky-600 dark:text-sky-400";
    const reportedAgo = (() => {
      try {
        return reading.reported_at
          ? ` · reported ${formatDistanceToNow(new Date(reading.reported_at), { addSuffix: true })}`
          : "";
      } catch { return ""; }
    })();
    return (
      <Badge
        variant="outline"
        className={`text-xs ${tone}`}
        title={`Battery voltage from the vehicle (Geotab)${v >= 13.2 ? " · charging" : ""}${reportedAgo}`}
      >
        <BatteryMedium className="w-3 h-3 mr-1" />
        {v.toFixed(1)}V
      </Badge>
    );
  };
  const COOLDOWN_DURATIONS = [5, 10, 15, 30, 60];

  // Filter patrol units only
  const patrolUnits = units.filter(u => u.is_patrol);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [carsRes, unitsRes, logsRes, cooldownsRes, todosRes, fuelRes] = await Promise.all([
        axios.get(`${API}/patrol/cars`, { withCredentials: true }),
        axios.get(`${API}/units`, { withCredentials: true }),
        axios.get(`${API}/patrol/logs`, { withCredentials: true }),
        axios.get(`${API}/patrol/cooldowns/all`, { withCredentials: true }).catch(() => ({ data: { cooldowns: [] } })),
        axios.get(`${API}/patrol/todos`, { withCredentials: true }).catch(() => ({ data: { todos: [] } })),
        // Swallowed on purpose: a Geotab problem must not turn into a failed
        // FSD load, it just means no vehicle reading is shown this cycle.
        axios.get(`${API}/geotab/fuel`, { withCredentials: true }).catch(() => ({ data: { fuel: {} } }))
      ]);
      setCars(carsRes.data);
      setUnits(unitsRes.data);
      setLogs(logsRes.data.slice(0, 100));
      setActiveCooldowns(cooldownsRes.data.cooldowns || []);
      setGeotabFuel(fuelRes.data?.fuel || {});
      setGeotabVoltage(fuelRes.data?.voltage || {});
      setTodos(todosRes.data?.todos || []);
    } catch (error) {
      toast.error("Failed to load patrol data");
    } finally {
      setLoading(false);
    }
  };

  const refreshCars = async () => {
    try {
      const response = await axios.get(`${API}/patrol/cars`, { withCredentials: true });
      setCars(response.data);
    } catch (error) {
      toast.error("Failed to refresh cars");
    }
  };

  // Todo CRUD
  const fetchTodos = async () => {
    try {
      const res = await axios.get(`${API}/patrol/todos`, { withCredentials: true });
      setTodos(res.data?.todos || []);
    } catch (error) {
      toast.error("Failed to load todos");
    }
  };

  const handleCreateTodo = async () => {
    if (!newTodoText.trim()) return;
    try {
      await axios.post(`${API}/patrol/todos`, { text: newTodoText.trim() }, { withCredentials: true });
      setNewTodoText("");
      fetchTodos();
    } catch (error) {
      toast.error("Failed to create todo");
    }
  };

  const handleToggleTodo = async (todo) => {
    try {
      await axios.put(`${API}/patrol/todos/${todo.todo_id}`, { completed: !todo.completed }, { withCredentials: true });
      fetchTodos();
    } catch (error) {
      toast.error("Failed to update todo");
    }
  };

  const handleEditTodo = async (todoId) => {
    if (!editingTodoText.trim()) return;
    try {
      await axios.put(`${API}/patrol/todos/${todoId}`, { text: editingTodoText.trim() }, { withCredentials: true });
      setEditingTodoId(null);
      setEditingTodoText("");
      fetchTodos();
    } catch (error) {
      toast.error("Failed to edit todo");
    }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      await axios.delete(`${API}/patrol/todos/${todoId}`, { withCredentials: true });
      if (expandedTodo === todoId) setExpandedTodo(null);
      fetchTodos();
    } catch (error) {
      toast.error("Failed to delete todo");
    }
  };

  const handleAddUpdate = async (todoId) => {
    if (!newUpdateText.trim()) return;
    try {
      await axios.post(`${API}/patrol/todos/${todoId}/updates`, { message: newUpdateText.trim() }, { withCredentials: true });
      setNewUpdateText("");
      fetchTodos();
    } catch (error) {
      toast.error("Failed to add update");
    }
  };

  const handleLogin = async () => {
    if (!selectedUnit || !selectedCar) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/login`,
        { unit_name: selectedUnit },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowLoginDialog(false);
      setSelectedUnit("");
      toast.success(`${selectedUnit} logged into ${selectedCar.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to login");
    }
  };

  const handleLoginMultiple = async () => {
    if (selectedUnitsToLogin.length === 0 || !selectedCar) return;
    try {
      // First unit becomes primary (login)
      const primaryUnit = selectedUnitsToLogin[0];
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/login`,
        { unit_name: primaryUnit },
        { withCredentials: true }
      );
      
      // Rest of the units join
      let updatedCar = response.data;
      for (let i = 1; i < selectedUnitsToLogin.length; i++) {
        try {
          const joinResponse = await axios.post(
            `${API}/patrol/cars/${selectedCar.car_id}/join`,
            { unit_name: selectedUnitsToLogin[i] },
            { withCredentials: true }
          );
          updatedCar = joinResponse.data;
        } catch (joinError) {
          toast.error(`Failed to add ${selectedUnitsToLogin[i]}: ${joinError.response?.data?.detail || 'Unknown error'}`);
        }
      }
      
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? updatedCar : c));
      setShowLoginDialog(false);
      setSelectedUnitsToLogin([]);
      setUnitSearchQuery("");
      
      if (selectedUnitsToLogin.length === 1) {
        toast.success(`${primaryUnit} logged into ${selectedCar.name}`);
      } else {
        toast.success(`${selectedUnitsToLogin.length} units logged into ${selectedCar.name}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to login");
    }
  };

  // Google Maps link for a parked spot that came off the vehicle's own
  // position — mirrors the Patrol tab. A typed-in note stays plain text.
  const parkedLocationMapHref = (car) => {
    if (!car.parked_location) return null;
    if (!["business", "address"].includes(car.parked_location_source)) return null;
    if (car.parked_location_lat != null && car.parked_location_lng != null) {
      return `https://www.google.com/maps/search/?api=1&query=${car.parked_location_lat},${car.parked_location_lng}`;
    }
    const plain = car.parked_location.replace(/\s*\[[^\]]*\]\s*$/, "");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(plain + ", Brooklyn, NY")}`;
  };

  const handleParkedLocationChange = (text, meta = null) => {
    setParkedLocation(text);
    setParkedLocationMeta(meta || null);
  };

  const handleLogout = async () => {
    if (!parkedLocation.trim() || !selectedCar) {
      toast.error("Parked location is required");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/logout`,
        {
          parked_location: parkedLocation,
          note: logoutNote || null,
          fuel_level: logoutFuelLevel || null,
          parked_location_source: parkedLocationMeta?.source || null,
          parked_location_lat: parkedLocationMeta?.lat ?? null,
          parked_location_lng: parkedLocationMeta?.lng ?? null,
        },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowLogoutDialog(false);
      setParkedLocation("");
      setParkedLocationMeta(null);
      setLogoutNote("");
      setLogoutFuelLevel("");
      toast.success(`Logged out of ${selectedCar.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to logout");
    }
  };

  const handleJoin = async () => {
    if (!selectedUnit || !selectedCar) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/join`,
        { unit_name: selectedUnit },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowJoinDialog(false);
      setSelectedUnit("");
      toast.success(`${selectedUnit} joined ${selectedCar.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join");
    }
  };

  const handleLeave = async (unitName) => {
    if (!selectedCar) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/leave`,
        { unit_name: unitName },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowLeaveDialog(false);
      toast.success(`${unitName} left ${selectedCar.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave");
    }
  };

  const handleTransfer = async () => {
    if (!selectedUnit || !selectedCar) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/transfer`,
        { unit_name: selectedUnit },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowTransferDialog(false);
      setSelectedUnit("");
      toast.success(`Assignment transferred to ${selectedUnit}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer");
    }
  };

  const handleOutOfService = async () => {
    if (!outOfServiceNote.trim() || !selectedCar) {
      toast.error("Reason is required");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/out-of-service`,
        { note: outOfServiceNote },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowOutOfServiceDialog(false);
      setOutOfServiceNote("");
      toast.success(`${selectedCar.name} marked out of service`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set out of service");
    }
  };

  const handleBackInService = async (car) => {
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${car.car_id}/back-in-service`,
        {},
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === car.car_id ? response.data : c));
      toast.success(`${car.name} is back in service`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set back in service");
    }
  };

  const handleToggleHidden = async () => {
    if (!hideAction) return;
    const { car, hide } = hideAction;
    try {
      const response = await axios.put(
        `${API}/patrol/cars/${car.car_id}/hidden`,
        { hidden: hide },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === car.car_id ? response.data : c));
      toast.success(hide ? `${car.name} hidden from Patrol tab` : `${car.name} visible in Patrol tab`);
      setShowHideConfirmDialog(false);
      setHideAction(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update visibility");
    }
  };

  const handleUpdateLocation = async () => {
    if (!editParkedLocation.trim() || !selectedCar) {
      toast.error("Location is required");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/update-location`,
        {
          parked_location: editParkedLocation,
          parked_location_source: editParkedLocationMeta?.source || null,
          parked_location_lat: editParkedLocationMeta?.lat ?? null,
          parked_location_lng: editParkedLocationMeta?.lng ?? null,
        },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowEditLocationDialog(false);
      setEditParkedLocation("");
      setEditParkedLocationMeta(null);
      toast.success("Location updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update location");
    }
  };

  const handleUpdateFuel = async () => {
    if (!editFuelLevel || !selectedCar) {
      toast.error("Fuel level is required");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/update-fuel`,
        { fuel_level: editFuelLevel },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowEditFuelDialog(false);
      setEditFuelLevel("");
      toast.success("Fuel level updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update fuel level");
    }
  };

  // Reserve a car (admin)
  const handleReserve = async () => {
    if (!selectedCar) return;
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${selectedCar.car_id}/reserve`,
        {
          duration_minutes: reserveDuration,
          unit_name: reserveForUnit && reserveForUnit !== "__none__" ? reserveForUnit : null
        },
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === selectedCar.car_id ? response.data : c));
      setShowReserveDialog(false);
      setReserveDuration(15);
      setReserveForUnit("");
      toast.success(`Reserved ${selectedCar.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reserve");
    }
  };

  // Cancel reservation (admin)
  const handleCancelReservation = async (car) => {
    try {
      const response = await axios.post(
        `${API}/patrol/cars/${car.car_id}/cancel-reservation`,
        {},
        { withCredentials: true }
      );
      setCars(cars.map(c => c.car_id === car.car_id ? response.data : c));
      toast.success("Reservation cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel reservation");
    }
  };

  // Apply cooldown (admin)
  const handleApplyCooldown = async () => {
    if (!cooldownUnit) {
      toast.error("Select a unit");
      return;
    }
    try {
      await axios.post(
        `${API}/patrol/cooldowns/apply`,
        {
          unit_name: cooldownUnit,
          car_id: cooldownCarId === "__all__" ? null : cooldownCarId,
          duration_minutes: cooldownDuration
        },
        { withCredentials: true }
      );
      setShowCooldownDialog(false);
      setCooldownUnit("");
      setCooldownDuration(15);
      setCooldownCarId("__all__");
      toast.success(`Cooldown applied to ${cooldownUnit}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply cooldown");
    }
  };

  // Clear cooldown (admin)
  const handleClearCooldown = async (unitName, carId) => {
    try {
      await axios.delete(
        `${API}/patrol/cooldowns/${unitName}${carId ? `?car_id=${carId}` : ""}`,
        { withCredentials: true }
      );
      toast.success(`Cooldown cleared for ${unitName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear cooldown");
    }
  };

  // Format countdown timer
  const formatCountdown = (expiresAt) => {
    if (!expiresAt) return "";
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    if (diff <= 0) return "Expired";
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "available":
        return <Badge className="bg-emerald-600 dark:bg-emerald-900/50 text-white dark:text-emerald-200"><CheckCircle className="w-3 h-3 mr-1" />Available</Badge>;
      case "active_patrol":
        return <Badge className="bg-blue-600 dark:bg-blue-900/50 text-white dark:text-blue-200"><Car className="w-3 h-3 mr-1" />Active Patrol</Badge>;
      case "out_of_service":
        return <Badge className="bg-red-600 dark:bg-red-900/50 text-white dark:text-red-200"><AlertTriangle className="w-3 h-3 mr-1" />Out of Service</Badge>;
      case "reserved":
        return <Badge className="bg-amber-600 dark:bg-amber-900/50 text-white dark:text-amber-200"><Lock className="w-3 h-3 mr-1" />Reserved</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // A car filtered to that no longer exists is ignored, not "matches nothing".
  const activeCarFilter = carFilter.filter((id) => cars.some((c) => c.car_id === id));
  const filteredCars = cars.filter(car =>
    (activeCarFilter.length === 0 || activeCarFilter.includes(car.car_id)) && (
      car.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      car.assigned_unit?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // One fuel line for every card, whatever its status (it used to be three
  // copies that had drifted apart): the reported level, Geotab's reading, edit.
  const fuelLine = (car) => (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1 flex-wrap">
        {car.fuel_level ? (
          <Badge variant="outline" className={`text-xs ${
            car.fuel_level === "Empty" || car.fuel_level === "1/8" ? "border-red-500 text-red-400" :
            car.fuel_level === "1/4" || car.fuel_level === "3/8" ? "border-amber-500 text-amber-400" :
            "border-green-500 text-green-400"
          }`}>
            <Fuel className="w-3 h-3 mr-1" />{car.fuel_level}
          </Badge>
        ) : (
          <span className="text-xs text-slate-500 flex items-center gap-1"><Fuel className="w-3 h-3" />No fuel data</span>
        )}
        {geotabFuelBadge(car.car_id)}
        {geotabVoltageBadge(car.car_id)}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-slate-400"
        title="Edit fuel level"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedCar(car);
          setEditFuelLevel(car.fuel_level || "");
          setShowEditFuelDialog(true);
        }}
      >
        <Pencil className="w-3 h-3" />
      </Button>
    </div>
  );

  const canManageOutOfService = true; // Anyone with page access can mark out of service

  // Send a remote unlock to the DroneMobile vehicle linked to a patrol car.
  // On FSD this is available for any linked car regardless of who (if anyone)
  // is logged into it — the backend authorizes it via patrol admin access.
  const handleDroneUnlock = async (car) => {
    if (!car) return;
    setUnlockingCarId(car.car_id);
    try {
      const res = await axios.post(
        `${API}/patrol/cars/${car.car_id}/unlock-drone`,
        { source: "fsd" },
        { withCredentials: true }
      );
      toast.success(`Unlock sent to ${res.data?.vehicle_name || car.name}`);
      setShowDroneUnlockDialog(false);
      setDroneUnlockCar(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unlock vehicle");
    } finally {
      setUnlockingCarId(null);
    }
  };

  // Remote lock — same rules and confirm as unlock.
  const handleDroneLock = async (car) => {
    if (!car) return;
    setLockingCarId(car.car_id);
    try {
      const res = await axios.post(
        `${API}/patrol/cars/${car.car_id}/lock-drone`,
        { source: "fsd" },
        { withCredentials: true }
      );
      toast.success(`Lock sent to ${res.data?.vehicle_name || car.name}`);
      setDroneLockCar(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to lock vehicle");
    } finally {
      setLockingCarId(null);
    }
  };

  // Export activity logs to CSV (admin only)
  const handleExportActivityLog = () => {
    if (logs.length === 0) {
      toast.error("No activity logs to export");
      return;
    }
    
    const headers = ["Date/Time", "Car", "Unit", "Action", "Location", "Duration (min)", "Fuel Level", "Note", "Performed By"];
    const csvRows = [headers.join(",")];
    
    logs.forEach(log => {
      const row = [
        format(new Date(log.timestamp), "yyyy-MM-dd HH:mm:ss"),
        log.car_name,
        log.unit_name,
        log.action,
        log.parked_location || log.old_location || "",
        log.session_duration_minutes || "",
        log.fuel_level || "",
        log.logout_note || log.out_of_service_note || "",
        log.performed_by_name
      ].map(val => `"${String(val).replace(/"/g, '""')}"`);
      csvRows.push(row.join(","));
    });
    
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `patrol_activity_log_${format(new Date(), "yyyy-MM-dd_HHmm")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Activity log exported");
  };

  // Compute completed sessions from logs
  const sessions = logs.filter(l => ["logout", "leave"].includes(l.action) && (l.session_duration_minutes || 0) > 0);

  // Fuel at each end of a session. The end is recorded on the logout itself;
  // the start is the last fuel level recorded for that car BEFORE the session
  // began — the previous logout, or an FSD fuel edit — so it's known for old
  // sessions too, with nothing new stored.
  const fuelHistory = {};
  for (const l of logs) {
    if (l.car_id && l.fuel_level && l.timestamp) (fuelHistory[l.car_id] ||= []).push(l);
  }
  for (const id in fuelHistory) fuelHistory[id].sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1));
  const sessionStartIso = (s) => s.session_start
    || new Date(new Date(s.timestamp).getTime() - (s.session_duration_minutes || 0) * 60000).toISOString();
  const fuelBefore = (carId, iso) => {
    const list = fuelHistory[carId] || [];
    const t = new Date(iso).getTime();
    let found = null;
    for (const l of list) { if (new Date(l.timestamp).getTime() < t) found = l.fuel_level; else break; }
    return found;
  };
  const fuelTone = (f) => !f ? "text-slate-500"
    : f === "Empty" || f === "1/8" ? "text-red-400"
    : f === "1/4" || f === "3/8" ? "text-amber-400"
    : "text-green-400";

  // Edit session helper - compute duration preview
  const editDurationPreview = (() => {
    if (!editSessionData.session_start || !editSessionData.timestamp) return null;
    const start = new Date(editSessionData.session_start);
    const end = new Date(editSessionData.timestamp);
    const mins = Math.max(0, Math.round((end - start) / 60000));
    return mins;
  })();

  const handleEditSession = async () => {
    if (!selectedSession) return;
    try {
      const payload = {};
      if (editSessionData.unit_name && editSessionData.unit_name !== selectedSession.unit_name) payload.unit_name = editSessionData.unit_name;
      if (editSessionData.car_id && editSessionData.car_id !== selectedSession.car_id) {
        payload.car_id = editSessionData.car_id;
        const car = cars.find(c => c.car_id === editSessionData.car_id);
        if (car) payload.car_name = car.name;
      }
      if (editSessionData.session_start) payload.session_start = new Date(editSessionData.session_start).toISOString();
      if (editSessionData.timestamp) payload.timestamp = new Date(editSessionData.timestamp).toISOString();

      if (Object.keys(payload).length === 0) {
        toast.error("No changes made");
        return;
      }

      await axios.put(`${API}/patrol/logs/${selectedSession.log_id}`, payload, { withCredentials: true });
      toast.success("Session updated");
      setShowEditSessionDialog(false);
      setSelectedSession(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update session");
    }
  };

  const handleDeleteSession = async () => {
    if (!selectedSession) return;
    try {
      await axios.delete(`${API}/patrol/logs/${selectedSession.log_id}`, { withCredentials: true });
      toast.success("Session deleted");
      setShowDeleteSessionDialog(false);
      setSelectedSession(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete session");
    }
  };

  const handleCreateSession = async () => {
    if (!editSessionData.unit_name || !editSessionData.car_id || !editSessionData.session_start || !editSessionData.timestamp) return;
    try {
      const car = cars.find(c => c.car_id === editSessionData.car_id);
      await axios.post(`${API}/patrol/logs/manual`, {
        unit_name: editSessionData.unit_name,
        car_id: editSessionData.car_id,
        car_name: car?.name || editSessionData.car_name,
        session_start: new Date(editSessionData.session_start).toISOString(),
        timestamp: new Date(editSessionData.timestamp).toISOString()
      }, { withCredentials: true });
      toast.success("Session created");
      setShowEditSessionDialog(false);
      setEditSessionData({ unit_name: "", car_id: "", car_name: "", session_start: "", timestamp: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create session");
    }
  };

  // Format datetime-local value from ISO string (NY timezone)
  const toLocalDatetime = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(d);
    const get = (type) => parts.find(p => p.type === type).value;
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-wider">Patrol - Admin</h1>
          <p className="text-sm text-slate-400">Manage patrol car assignments</p>
        </div>
      )}

      {/* One toolbar: search, then the vehicle filter, notification switches
          and refresh — on one line, wrapping only on a narrow phone. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* The search only filters the Cars list, so it's only there. */}
        {activeTab === "cars" ? (
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search cars or units..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-dark h-9"
            />
          </div>
        ) : <div className="flex-1" />}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`h-9 ${activeCarFilter.length > 0 ? "border-blue-500 text-blue-400" : ""}`}
            >
              <Filter className="w-4 h-4 mr-1" />
              {activeCarFilter.length > 0 ? `${activeCarFilter.length} vehicle${activeCarFilter.length === 1 ? "" : "s"}` : "Vehicles"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2 bg-slate-900 border-slate-700" align="end">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Filter vehicles</span>
              {activeCarFilter.length > 0 && (
                <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => saveCarFilter([])}>
                  Clear
                </Button>
              )}
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {cars.map((car) => (
                <div
                  key={car.car_id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-800 ${activeCarFilter.includes(car.car_id) ? "bg-slate-800" : ""}`}
                  onClick={() => toggleCarFilter(car.car_id)}
                >
                  <Checkbox checked={activeCarFilter.includes(car.car_id)} className="pointer-events-none" />
                  <span className="text-sm font-mono">{car.name}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {notifSettings && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`h-9 ${Object.values(notifSettings).some((on) => on === false) ? "border-amber-500 text-amber-500" : ""}`}
                title="Patrol notifications"
              >
                <Bell className="w-4 h-4 mr-1" />Notifications
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3 bg-slate-900 border-slate-700" align="end">
              <p className="text-sm font-medium mb-1">Patrol notifications</p>
              <p className="text-[11px] text-slate-400 mb-3">Logins, logouts, transfers, reservations — for everyone.</p>
              {/* Only the switches this server has (CCHD: push + WhatsApp;
                  app8 sends patrol events by push only). */}
              {[["push_enabled", "Push notifications"], ["whatsapp_enabled", "WhatsApp (vehicle groups)"]]
                .filter(([key]) => key in notifSettings).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 py-1.5 cursor-pointer">
                  <Checkbox
                    checked={!!notifSettings[key]}
                    onCheckedChange={(v) => setNotifSwitch(key, v === true)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </PopoverContent>
          </Popover>
        )}
        <Button variant="outline" size="sm" className="h-9" onClick={refreshCars}>
          <RefreshCw className="w-4 h-4 mr-1" />Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <TabsList className="bg-slate-800 min-w-max sm:min-w-0">
            <TabsTrigger value="cars"><Car className="w-3 h-3 mr-1" />Cars ({activeCarFilter.length > 0 ? `${filteredCars.length}/${cars.length}` : cars.length})</TabsTrigger>
            <TabsTrigger value="logs"><Clock className="w-3 h-3 mr-1" />Activity</TabsTrigger>
            <TabsTrigger value="cooldowns"><Ban className="w-3 h-3 mr-1" />Cooldowns ({activeCooldowns.length})</TabsTrigger>
            <TabsTrigger value="notes"><MessageSquare className="w-3 h-3 mr-1" />Notes ({logs.filter(l => l.logout_note).length})</TabsTrigger>
            <TabsTrigger value="sessions"><ClipboardList className="w-3 h-3 mr-1" />Sessions ({sessions.length})</TabsTrigger>
            <TabsTrigger value="todos"><CheckSquare className="w-3 h-3 mr-1" />To Do ({todos.filter(t => !t.completed).length})</TabsTrigger>
            <TabsTrigger value="receipts"><Receipt className="w-3 h-3 mr-1" />Receipts{receiptsUnpaid ? ` (${receiptsUnpaid})` : ""}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="cars">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCars.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Car className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No patrol cars found</p>
                <p className="text-sm mt-1">Cars are managed in Settings → Patrol Cars</p>
              </div>
            ) : (
              filteredCars.map((car) => (
                <Card key={car.car_id} className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-mono">{car.name}</CardTitle>
                      {getStatusBadge(car.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {fuelLine(car)}

                    {car.status === "active_patrol" && (
                      <>
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800/50">
                          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                            <Users className="w-4 h-4" />
                            <span className="font-semibold">{car.assigned_unit}</span>
                            <span className="text-xs text-slate-400">(Primary)</span>
                          </div>
                          {car.joined_units?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {car.joined_units.map(unit => (
                                <Badge key={unit} variant="outline" className="text-xs">{unit}</Badge>
                              ))}
                            </div>
                          )}
                          {car.assignment_started_at && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                              <Clock className="w-3 h-3" />
                              Started {formatDistanceToNow(new Date(car.assignment_started_at), { addSuffix: true })}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => { setSelectedCar(car); setShowJoinDialog(true); }}>
                            <UserPlus className="w-3 h-3 mr-1" />Join
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedCar(car); setShowLeaveDialog(true); }} disabled={!car.joined_units?.length}>
                            <UserMinus className="w-3 h-3 mr-1" />Leave
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedCar(car); setShowTransferDialog(true); }}>
                            <ArrowRightLeft className="w-3 h-3 mr-1" />Transfer
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => { setSelectedCar(car); setShowLogoutDialog(true); }}>
                            <LogOut className="w-3 h-3 mr-1" />Logout
                          </Button>
                        </div>
                      </>
                    )}

                    {car.status === "available" && (
                      <>
                        {car.parked_location ? (
                          <div className="flex items-center justify-between text-sm text-slate-400">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              {(() => {
                                const href = parkedLocationMapHref(car);
                                return href ? (
                                  <span>Parked at: <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{car.parked_location}</a></span>
                                ) : (
                                  <span>Parked at: {car.parked_location}</span>
                                );
                              })()}
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0"
                              onClick={() => { 
                                setSelectedCar(car); 
                                setEditParkedLocation(car.parked_location || ""); 
                                setShowEditLocationDialog(true); 
                              }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-slate-400"
                            onClick={() => { 
                              setSelectedCar(car); 
                              setEditParkedLocation(""); 
                              setShowEditLocationDialog(true); 
                            }}
                          >
                            <MapPin className="w-4 h-4 mr-1" />Add Location
                          </Button>
                        )}
                        <div className="flex gap-2">
                          <Button className="flex-1 btn-primary" onClick={() => { setSelectedCar(car); setShowLoginDialog(true); }}>
                            <LogIn className="w-4 h-4 mr-2" />Login
                          </Button>
                          <Button variant="outline" onClick={() => { setSelectedCar(car); setReserveForUnit(""); setReserveDuration(15); setShowReserveDialog(true); }}>
                            <Timer className="w-4 h-4 mr-1" />Reserve
                          </Button>
                        </div>
                      </>
                    )}

                    {car.status === "reserved" && (
                      <>
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800/50">
                          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                            <Lock className="w-4 h-4" />
                            <span className="font-semibold">{car.reserved_by || "Admin Hold"}</span>
                          </div>
                          {car.reservation_expires_at ? (
                            <div className="flex items-center gap-1 mt-2 text-lg font-mono font-bold text-amber-400">
                              <Timer className="w-4 h-4" />
                              {formatCountdown(car.reservation_expires_at)}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 mt-2 text-sm text-amber-400">
                              <Lock className="w-3 h-3" />
                              No Timer (Indefinite)
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button className="flex-1 btn-primary" onClick={() => { setSelectedCar(car); setShowLoginDialog(true); }}>
                            <LogIn className="w-4 h-4 mr-2" />Login Now
                          </Button>
                          <Button variant="destructive" onClick={() => handleCancelReservation(car)}>
                            <X className="w-4 h-4 mr-1" />Cancel
                          </Button>
                        </div>
                      </>
                    )}

                    {car.status === "out_of_service" && (
                      <>
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800/50">
                          <p className="text-sm text-red-800 dark:text-red-200">{car.out_of_service_note}</p>
                        </div>
                        {canManageOutOfService && (
                          <Button className="w-full" variant="outline" onClick={() => handleBackInService(car)}>
                            <CheckCircle className="w-4 h-4 mr-2" />Return to Service
                          </Button>
                        )}
                      </>
                    )}

                    {/* The less-used controls, in one quiet row instead of three
                        full-width ones: remote unlock (any car linked to a
                        DroneMobile vehicle, whatever its status or who's in it),
                        out of service, and hiding the car from the Patrol tab. */}
                    <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-slate-200 dark:border-slate-800">
                      {car.drone_device_key && !user?.patrol_unlock_disabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={unlockingCarId === car.car_id}
                          className="h-7 px-2 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                          title={`Unlock ${car.drone_vehicle_name || car.name}`}
                          onClick={() => { setDroneUnlockCar(car); setShowDroneUnlockDialog(true); }}
                        >
                          <LockOpen className="w-3 h-3 mr-1" />
                          {unlockingCarId === car.car_id ? "Unlocking…" : "Unlock"}
                        </Button>
                      )}
                      {car.drone_device_key && !user?.patrol_unlock_disabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={lockingCarId === car.car_id}
                          className="h-7 px-2 text-xs text-sky-600 dark:text-sky-400 hover:bg-sky-500/10"
                          title={`Lock ${car.drone_vehicle_name || car.name}`}
                          onClick={() => setDroneLockCar(car)}
                        >
                          <Lock className="w-3 h-3 mr-1" />
                          {lockingCarId === car.car_id ? "Locking…" : "Lock"}
                        </Button>
                      )}
                      {car.status !== "out_of_service" && canManageOutOfService && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          onClick={() => { setSelectedCar(car); setShowOutOfServiceDialog(true); }}>
                          <AlertTriangle className="w-3 h-3 mr-1" />Out of Service
                        </Button>
                      )}
                      {canManageOutOfService && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className={`h-7 px-2 text-xs ml-auto ${car.hidden_from_patrol ? "text-amber-600 dark:text-amber-400" : "text-slate-500"}`}
                          title={car.hidden_from_patrol ? "Hidden from the Patrol tab — tap to show it again" : "Hide this car from the Patrol tab"}
                          onClick={() => {
                            setHideAction({ car, hide: !car.hidden_from_patrol });
                            setShowHideConfirmDialog(true);
                          }}
                        >
                          {car.hidden_from_patrol ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                          {car.hidden_from_patrol ? "Hidden from Patrol" : "Hide from Patrol"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="logs">
          <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                  {user?.role === "admin" && (
                    <Button variant="outline" size="sm" onClick={handleExportActivityLog} disabled={logs.length === 0}>
                      <Download className="w-4 h-4 mr-1" />Export CSV
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {logs.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No activity logged yet</p>
                  ) : (
                    <div className="space-y-2">
                      {logs.map((log) => (
                        <div key={log.log_id} className="p-2 sm:p-3 bg-slate-950 rounded border border-slate-800">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                              <Badge variant="outline" className="font-mono text-[10px] sm:text-xs px-1 sm:px-2">{log.car_name}</Badge>
                              <span className="font-semibold text-sm">{log.unit_name}</span>
                              <span className={`text-xs sm:text-sm ${
                                log.action === "reserve" || log.action === "cancel_reservation" || log.action === "reservation_expired"
                                  ? "text-amber-400" : "text-slate-400"
                              }`}>
                                {log.action === "login" && "logged in"}
                                {log.action === "logout" && "logged out"}
                                {log.action === "join" && "joined"}
                                {log.action === "leave" && "left"}
                                {log.action === "transfer" && "transferred"}
                                {log.action === "out_of_service" && "OOS"}
                                {log.action === "back_in_service" && "back in"}
                                {log.action === "location_update" && "loc. updated"}
                                {log.action === "fuel_update" && "fuel updated"}
                                {log.action === "reserve" && `reserved (${log.reservation_duration_minutes}m)`}
                                {log.action === "cancel_reservation" && "reservation cancelled"}
                                {log.action === "reservation_expired" && "reservation expired"}
                                {log.action === "hidden" && "hidden from patrol"}
                                {log.action === "shown" && "shown in patrol"}
                                {log.action === "session_created" && "session created"}
                                {log.action === "session_edited" && "session edited"}
                                {log.action === "session_deleted" && "session deleted"}
                              </span>
                            </div>
                            <span className="text-[10px] sm:text-xs text-slate-500">{format(new Date(log.timestamp), "M/d h:mma")}</span>
                          </div>
                          {log.action === "location_update" && log.old_location && (
                            <p className="text-sm text-slate-500 mt-1"><MapPin className="w-3 h-3 inline mr-1" />From: {log.old_location}</p>
                          )}
                          {log.parked_location && <p className="text-sm text-slate-400 mt-1"><MapPin className="w-3 h-3 inline mr-1" />{log.action === "location_update" ? "To: " : ""}{log.parked_location}</p>}
                          {log.session_duration_minutes && !["session_created", "session_edited", "session_deleted"].includes(log.action) && <p className="text-sm text-slate-400 mt-1"><Clock className="w-3 h-3 inline mr-1" />Session: {Math.floor(log.session_duration_minutes / 60)}h {log.session_duration_minutes % 60}m</p>}
                          {/* Fuel level display */}
                          {log.action === "fuel_update" && (
                            <p className="text-sm text-amber-400 mt-1"><Fuel className="w-3 h-3 inline mr-1" />Fuel: {log.old_fuel_level || "N/A"} → {log.fuel_level}</p>
                          )}
                          {log.action !== "fuel_update" && log.fuel_level && (
                            <p className="text-sm text-slate-400 mt-1"><Fuel className="w-3 h-3 inline mr-1" />Fuel: {log.fuel_level}</p>
                          )}
                          {log.logout_note && <p className="text-sm text-blue-400 mt-1">Note: {log.logout_note}</p>}
                          {log.out_of_service_note && <p className="text-sm text-red-400 mt-1">Reason: {log.out_of_service_note}</p>}
                          {/* Session management details */}
                          {(log.action === "session_created" || log.action === "session_edited" || log.action === "session_deleted") && log.session_duration_minutes && (
                            <p className={`text-sm mt-1 ${log.action === "session_deleted" ? "text-red-400" : "text-emerald-400"}`}>
                              <ClipboardList className="w-3 h-3 inline mr-1" />
                              {Math.floor(log.session_duration_minutes / 60)}h {log.session_duration_minutes % 60}m session
                            </p>
                          )}
                          <p className="text-xs text-slate-600 mt-1">by {log.performed_by_name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="cooldowns">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-red-400">Active Cooldowns</CardTitle>
                <Button variant="outline" size="sm" onClick={() => setShowCooldownDialog(true)}>
                  <Ban className="w-4 h-4 mr-1" />Apply Cooldown
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {activeCooldowns.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No active cooldowns</p>
              ) : (
                <div className="space-y-2">
                  {activeCooldowns.map((cd, idx) => (
                    <div key={idx} className="p-3 bg-red-950/30 rounded border border-red-800/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="font-semibold">{cd.unit_name}</span>
                          <span className="text-slate-400">
                            {cd.car_id ? `→ ${cd.car_name}` : "→ All Cars"}
                          </span>
                        </div>
                        <div className="text-sm text-red-400 mt-1">
                          <Timer className="w-3 h-3 inline mr-1" />
                          {formatCountdown(cd.cooldown_expires_at)} remaining ({cd.cooldown_minutes}m total)
                        </div>
                        {cd.applied_by_name && (
                          <p className="text-xs text-slate-500 mt-1">Applied by {cd.applied_by_name}</p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 self-end sm:self-auto flex-shrink-0" onClick={() => handleClearCooldown(cd.unit_name, cd.car_id)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-blue-400">Logout Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {logs.filter(log => log.logout_note).length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No logout notes submitted</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {logs.filter(log => log.logout_note).map((log) => (
                      <div key={log.log_id} className="p-3 bg-blue-950/30 rounded border border-blue-800/50">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline" className="font-mono text-xs">{log.car_name}</Badge>
                          <span className="text-xs text-slate-500">{format(new Date(log.timestamp), "MMM d, h:mm a")}</span>
                        </div>
                        <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">{log.logout_note}</p>
                        <p className="text-xs text-slate-500">by {log.performed_by_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-emerald-400">Completed Sessions</CardTitle>
                {(user?.role === "admin" || user?.session_admin_access) && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setEditSessionData({ unit_name: "", car_id: "", car_name: "", session_start: "", timestamp: "" });
                    setSelectedSession(null);
                    setShowEditSessionDialog(true);
                  }}>
                    <ClipboardList className="w-4 h-4 mr-1" />Create Session
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {sessions.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No completed sessions</p>
                ) : (
                  <div className="space-y-2">
                    {sessions.map((session) => (
                      <div key={session.log_id} className="p-3 bg-slate-950 rounded border border-slate-800">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="font-mono text-xs">{session.car_name}</Badge>
                            <span className="font-semibold text-sm">{session.unit_name}</span>
                            <Badge className={`text-xs ${session.action === "logout" ? "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300" : "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300"}`}>
                              {session.action === "logout" ? "Logout" : "Left"}
                            </Badge>
                          </div>
                          {(user?.role === "admin" || user?.session_admin_access) && (
                            <div className="flex gap-1 flex-shrink-0">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400" onClick={() => {
                                setSelectedSession(session);
                                // Estimate session_start from end time - duration if not stored
                                const estimatedStart = session.session_start
                                  ? session.session_start
                                  : new Date(new Date(session.timestamp).getTime() - (session.session_duration_minutes || 0) * 60000).toISOString();
                                setEditSessionData({
                                  unit_name: session.unit_name,
                                  car_id: session.car_id,
                                  car_name: session.car_name,
                                  session_start: toLocalDatetime(estimatedStart),
                                  timestamp: toLocalDatetime(session.timestamp)
                                });
                                setShowEditSessionDialog(true);
                              }}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-400" onClick={() => {
                                setSelectedSession(session);
                                setShowDeleteSessionDialog(true);
                              }}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-1 text-sm">
                          <div className="text-slate-400">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Start: {session.session_start
                              ? format(new Date(session.session_start), "M/d h:mma")
                              : format(new Date(new Date(session.timestamp).getTime() - (session.session_duration_minutes || 0) * 60000), "M/d h:mma")}
                          </div>
                          <div className="text-slate-400">
                            <Clock className="w-3 h-3 inline mr-1" />
                            End: {format(new Date(session.timestamp), "M/d h:mma")}
                          </div>
                          <div className="text-emerald-400 font-semibold">
                            <Timer className="w-3 h-3 inline mr-1" />
                            {Math.floor(session.session_duration_minutes / 60)}h {session.session_duration_minutes % 60}m
                          </div>
                        </div>
                        {session.logout_note && <p className="text-sm text-blue-400 mt-1">Note: {session.logout_note}</p>}
                        {/* Both ends of the session: where the car was taken
                            from, and where it was left. A session that started
                            on a transfer has no pickup spot. */}
                        {session.pickup_location && <p className="text-xs text-slate-500 mt-1"><MapPin className="w-3 h-3 inline mr-1" />Picked up: {session.pickup_location}</p>}
                        {session.parked_location && <p className="text-xs text-slate-500 mt-1"><MapPin className="w-3 h-3 inline mr-1" />Parked: {session.parked_location}</p>}
                        {(() => {
                          const startFuel = fuelBefore(session.car_id, sessionStartIso(session));
                          const endFuel = session.fuel_level || null;
                          if (!startFuel && !endFuel) return null;
                          return (
                            <p className="text-xs text-slate-500 mt-1">
                              <Fuel className="w-3 h-3 inline mr-1" />Fuel:{" "}
                              <span className={`font-semibold ${fuelTone(startFuel)}`}>{startFuel || "—"}</span>
                              <span className="mx-1">→</span>
                              <span className={`font-semibold ${fuelTone(endFuel)}`}>{endFuel || "—"}</span>
                            </p>
                          );
                        })()}
                        <p className="text-xs text-slate-600 mt-1">by {session.performed_by_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="receipts">
          <FuelReceipts cars={cars} user={user} onCountChange={setReceiptsUnpaid} />
        </TabsContent>

        <TabsContent value="todos">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-purple-400">To Do List</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* New todo input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Add a new task..."
                  value={newTodoText}
                  onChange={(e) => setNewTodoText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateTodo()}
                  className="input-dark"
                />
                <Button onClick={handleCreateTodo} disabled={!newTodoText.trim()} size="sm">
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>

              {/* Todo list */}
              <ScrollArea className="h-[500px]">
                {todos.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No tasks yet</p>
                ) : (
                  <div className="space-y-2">
                    {todos.map((todo) => (
                      <div key={todo.todo_id} className={`p-3 rounded border ${todo.completed ? "bg-slate-950/50 border-slate-800/50" : "bg-slate-950 border-slate-800"}`}>
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <button onClick={() => handleToggleTodo(todo)} className="mt-0.5 flex-shrink-0">
                            {todo.completed ? (
                              <CheckSquare className="w-5 h-5 text-green-500" />
                            ) : (
                              <Square className="w-5 h-5 text-slate-500 hover:text-purple-400" />
                            )}
                          </button>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {editingTodoId === todo.todo_id ? (
                              <div className="flex gap-2">
                                <Input
                                  value={editingTodoText}
                                  onChange={(e) => setEditingTodoText(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && handleEditTodo(todo.todo_id)}
                                  className="input-dark text-sm h-8"
                                  autoFocus
                                />
                                <Button size="sm" className="h-8" onClick={() => handleEditTodo(todo.todo_id)}>Save</Button>
                                <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingTodoId(null)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <p className={`text-sm ${todo.completed ? "line-through text-slate-500" : "text-slate-200"}`}>
                                {todo.text}
                              </p>
                            )}

                            {/* Meta info */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                              <span>Created by {todo.created_by_name} · {format(new Date(todo.created_at), "M/d h:mma")}</span>
                              {todo.completed && todo.completed_by_name && (
                                <span className="text-green-600">
                                  <CheckCircle className="w-3 h-3 inline mr-0.5" />
                                  Completed by {todo.completed_by_name} · {format(new Date(todo.completed_at), "M/d h:mma")}
                                </span>
                              )}
                              {todo.updates && todo.updates.length > 0 && (
                                <span className="text-blue-400">
                                  <MessageSquare className="w-3 h-3 inline mr-0.5" />
                                  {todo.updates.length} update{todo.updates.length !== 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400"
                              onClick={() => setExpandedTodo(expandedTodo === todo.todo_id ? null : todo.todo_id)}
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-slate-400 hover:text-yellow-400"
                              onClick={() => { setEditingTodoId(todo.todo_id); setEditingTodoText(todo.text); }}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>

                        {/* Expanded updates section */}
                        {expandedTodo === todo.todo_id && (
                          <div className="mt-3 ml-8 space-y-2">
                            <div className="border-l-2 border-slate-700 pl-3 space-y-2">
                              {todo.updates && todo.updates.length > 0 ? (
                                todo.updates.map((upd) => (
                                  <div key={upd.update_id} className="text-sm">
                                    <p className="text-slate-300">{upd.message}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {upd.created_by_name} · {format(new Date(upd.created_at), "M/d h:mma")}
                                    </p>
                                  </div>
                                ))
                              ) : (
                                <p className="text-xs text-slate-600">No updates yet</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add an update..."
                                value={newUpdateText}
                                onChange={(e) => setNewUpdateText(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAddUpdate(todo.todo_id)}
                                className="input-dark text-sm h-8"
                              />
                              <Button size="sm" className="h-8" onClick={() => handleAddUpdate(todo.todo_id)} disabled={!newUpdateText.trim()}>
                                <Send className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={(open) => { setShowLoginDialog(open); if (!open) { setSelectedUnit(""); setSelectedUnitsToLogin([]); setUnitSearchQuery(""); } }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Login to {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Search & Select Units</Label>
              <Input 
                value={unitSearchQuery} 
                onChange={(e) => setUnitSearchQuery(e.target.value)} 
                placeholder="Search units..." 
                className="input-dark mb-2" 
              />
              <ScrollArea className="h-48 border border-slate-700 rounded p-2">
                {patrolUnits.length === 0 ? (
                  <p className="text-xs text-amber-400 p-2">No units have patrol enabled. Enable in Units → Edit Unit.</p>
                ) : (
                  <div className="space-y-1">
                    {patrolUnits
                      .filter(u => u.name.toLowerCase().includes(unitSearchQuery.toLowerCase()))
                      .map((unit) => {
                        const isSelected = selectedUnitsToLogin.includes(unit.name);
                        const isPrimary = selectedUnitsToLogin[0] === unit.name;
                        return (
                          <div 
                            key={unit.unit_id}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedUnitsToLogin(selectedUnitsToLogin.filter(u => u !== unit.name));
                              } else {
                                setSelectedUnitsToLogin([...selectedUnitsToLogin, unit.name]);
                              }
                            }}
                            className={`p-2 rounded cursor-pointer flex items-center justify-between ${
                              isSelected ? 'bg-blue-600/30 border border-blue-500' : 'bg-slate-800 hover:bg-slate-700'
                            }`}
                          >
                            <span className="font-mono">{unit.name}</span>
                            {isPrimary && <Badge className="bg-blue-600 text-xs">Primary</Badge>}
                            {isSelected && !isPrimary && <Badge variant="outline" className="text-xs">Joining</Badge>}
                          </div>
                        );
                      })}
                  </div>
                )}
              </ScrollArea>
              {selectedUnitsToLogin.length > 0 && (
                <p className="text-xs text-slate-400 mt-2">
                  First selected unit ({selectedUnitsToLogin[0]}) will be primary. Others will join.
                </p>
              )}
            </div>
            <Button onClick={handleLoginMultiple} className="w-full btn-primary" disabled={selectedUnitsToLogin.length === 0}>
              <LogIn className="w-4 h-4 mr-2" />
              Login {selectedUnitsToLogin.length > 0 && `(${selectedUnitsToLogin.length} unit${selectedUnitsToLogin.length > 1 ? 's' : ''})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Logout Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={(open) => { setShowLogoutDialog(open); if (!open) { setParkedLocation(""); setParkedLocationMeta(null); setLogoutNote(""); setLogoutFuelLevel(""); setShowLocationPicker(false); setShowFuelPicker(false); } }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Logout from {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <ParkedLocationField
              value={parkedLocation}
              onPick={() => setShowLocationPicker(true)}
            />
            <FuelLevelField
              carId={selectedCar?.car_id}
              allowVehicleReading
              value={logoutFuelLevel}
              onChange={setLogoutFuelLevel}
              onManual={() => setShowFuelPicker(true)}
              label="Fuel Level (Optional)"
            />
            <div>
              <Label>Note (Optional)</Label>
              <Input value={logoutNote} onChange={(e) => setLogoutNote(e.target.value)} placeholder="Any notes about this session..." className="input-dark" />
            </div>
            <Button onClick={handleLogout} className="w-full" variant="destructive" disabled={!parkedLocation.trim()}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location picker — its own popup, a sibling of the logout dialog. */}
      <ParkedLocationDialog
        open={showLocationPicker}
        onOpenChange={setShowLocationPicker}
        carId={selectedCar?.car_id}
        value={parkedLocation}
        onChange={handleParkedLocationChange}
      />
      <FuelLevelDialog
        open={showFuelPicker}
        onOpenChange={setShowFuelPicker}
        value={logoutFuelLevel}
        onChange={setLogoutFuelLevel}
      />
      <FuelLevelDialog
        open={showEditFuelPicker}
        onOpenChange={setShowEditFuelPicker}
        value={editFuelLevel}
        onChange={setEditFuelLevel}
      />
      <ParkedLocationDialog
        open={showEditLocationPicker}
        onOpenChange={setShowEditLocationPicker}
        carId={selectedCar?.car_id}
        value={editParkedLocation}
        onChange={(text, meta = null) => { setEditParkedLocation(text); setEditParkedLocationMeta(meta || null); }}
      />

      {/* Join Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Join {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Currently assigned to: {selectedCar?.assigned_unit}</p>
            <div>
              <Label>Select Unit to Join</Label>
              <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                <SelectTrigger className="input-dark"><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  {patrolUnits.filter(u => u.name !== selectedCar?.assigned_unit && !selectedCar?.joined_units?.includes(u.name)).map((unit) => (
                    <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleJoin} className="w-full btn-primary" disabled={!selectedUnit}><UserPlus className="w-4 h-4 mr-2" />Join Patrol</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Leave {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Select unit to remove from this patrol:</p>
            <div className="space-y-2">
              {selectedCar?.joined_units?.map(unit => (
                <Button key={unit} variant="outline" className="w-full justify-start" onClick={() => handleLeave(unit)}>
                  <UserMinus className="w-4 h-4 mr-2" />{unit}
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Transfer Assignment - {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Transfer primary assignment from {selectedCar?.assigned_unit} to another unit.</p>
            <div>
              <Label>Transfer To</Label>
              <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                <SelectTrigger className="input-dark"><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  {patrolUnits.filter(u => u.name !== selectedCar?.assigned_unit).map((unit) => (
                    <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}{selectedCar?.joined_units?.includes(unit.name) && " (joined)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleTransfer} className="w-full btn-primary" disabled={!selectedUnit}><ArrowRightLeft className="w-4 h-4 mr-2" />Transfer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Out of Service Dialog */}
      <Dialog open={showOutOfServiceDialog} onOpenChange={setShowOutOfServiceDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="text-red-400">Mark {selectedCar?.name} Out of Service</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedCar?.status === "active_patrol" && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800/50">
                <p className="text-sm text-amber-800 dark:text-amber-200">Warning: {selectedCar.assigned_unit} will be logged out.</p>
              </div>
            )}
            <div>
              <Label>Reason (Required)</Label>
              <Textarea value={outOfServiceNote} onChange={(e) => setOutOfServiceNote(e.target.value)} placeholder="Why is this car out of service?" className="input-dark" />
            </div>
            <Button onClick={handleOutOfService} className="w-full" variant="destructive" disabled={!outOfServiceNote.trim()}><AlertTriangle className="w-4 h-4 mr-2" />Mark Out of Service</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hide from Patrol Confirmation Dialog */}
      <Dialog open={showHideConfirmDialog} onOpenChange={(open) => { setShowHideConfirmDialog(open); if (!open) setHideAction(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle>
              {hideAction?.hide ? "Hide from Patrol Tab" : "Show in Patrol Tab"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-300">
              {hideAction?.hide
                ? `Are you sure you want to hide ${hideAction?.car?.name} from the Patrol tab? Users will not see this car until it's brought back in service or unhidden.`
                : `Are you sure you want to show ${hideAction?.car?.name} in the Patrol tab again?`
              }
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setShowHideConfirmDialog(false); setHideAction(null); }}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                variant={hideAction?.hide ? "destructive" : "default"}
                onClick={handleToggleHidden}
              >
                {hideAction?.hide ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {hideAction?.hide ? "Hide" : "Show"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drone Unlock Confirm Dialog */}
      <Dialog open={showDroneUnlockDialog} onOpenChange={(open) => { setShowDroneUnlockDialog(open); if (!open) setDroneUnlockCar(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle>Unlock {droneUnlockCar?.drone_vehicle_name || droneUnlockCar?.name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-300">
              This sends a remote unlock command to {droneUnlockCar?.drone_vehicle_name || droneUnlockCar?.name}
              {droneUnlockCar?.assigned_unit ? ` (currently assigned to ${droneUnlockCar.assigned_unit})` : ""}.
              It will be recorded in the command history.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setShowDroneUnlockDialog(false); setDroneUnlockCar(null); }}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={unlockingCarId === droneUnlockCar?.car_id}
                onClick={() => handleDroneUnlock(droneUnlockCar)}
              >
                <LockOpen className="w-4 h-4 mr-2" />
                {unlockingCarId === droneUnlockCar?.car_id ? "Unlocking…" : "Unlock"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drone Lock Confirm Dialog */}
      <Dialog open={!!droneLockCar} onOpenChange={(open) => { if (!open) setDroneLockCar(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle>Lock {droneLockCar?.drone_vehicle_name || droneLockCar?.name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-300">
              This sends a remote lock command to {droneLockCar?.drone_vehicle_name || droneLockCar?.name}
              {droneLockCar?.assigned_unit ? ` (currently assigned to ${droneLockCar.assigned_unit})` : ""}.
              It will be recorded in the command history.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDroneLockCar(null)}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-sky-600 hover:bg-sky-700"
                disabled={lockingCarId === droneLockCar?.car_id}
                onClick={() => handleDroneLock(droneLockCar)}
              >
                <Lock className="w-4 h-4 mr-2" />
                {lockingCarId === droneLockCar?.car_id ? "Locking…" : "Lock"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={showEditLocationDialog} onOpenChange={(open) => { setShowEditLocationDialog(open); if (!open) { setEditParkedLocation(""); setEditParkedLocationMeta(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Edit Parked Location - {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Same field as logout: the businesses and address nearest the
                car's own position, rather than a box to type a landmark into. */}
            <ParkedLocationField
              value={editParkedLocation}
              onPick={() => setShowEditLocationPicker(true)}
            />
            <Button onClick={handleUpdateLocation} className="w-full btn-primary" disabled={!editParkedLocation.trim()}>
              <MapPin className="w-4 h-4 mr-2" />Save Location
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Fuel Level Dialog */}
      <Dialog open={showEditFuelDialog} onOpenChange={(open) => { setShowEditFuelDialog(open); if (!open) setEditFuelLevel(""); }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Update Fuel Level - {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Same field as the FSD logout: confirm what the vehicle reports,
                or set it by hand. It replaced a bare dropdown, which couldn't
                offer the reading at all. */}
            <FuelLevelField
              carId={selectedCar?.car_id}
              allowVehicleReading
              value={editFuelLevel}
              onChange={setEditFuelLevel}
              onManual={() => setShowEditFuelPicker(true)}
              label="Fuel Level"
            />
            <Button onClick={handleUpdateFuel} className="w-full btn-primary" disabled={!editFuelLevel}>
              <Fuel className="w-4 h-4 mr-2" />Save Fuel Level
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reserve Dialog (Admin) */}
      <Dialog open={showReserveDialog} onOpenChange={(open) => { setShowReserveDialog(open); if (!open) { setReserveDuration(15); setReserveForUnit(""); } }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>Reserve {selectedCar?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Reserve For Unit (optional - leave blank for Admin Hold)</Label>
              <Select value={reserveForUnit} onValueChange={setReserveForUnit}>
                <SelectTrigger className="input-dark">
                  <SelectValue placeholder="Admin Hold (no unit)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Admin Hold (no unit)</SelectItem>
                  {patrolUnits.map((unit) => (
                    <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={reserveDuration.toString()} onValueChange={(v) => setReserveDuration(parseInt(v))}>
                <SelectTrigger className="input-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="0">No Timer (Admin Hold)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleReserve} className="w-full btn-primary">
              <Timer className="w-4 h-4 mr-2" />Reserve
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit/Create Session Dialog */}
      <Dialog open={showEditSessionDialog} onOpenChange={(open) => { setShowEditSessionDialog(open); if (!open) { setSelectedSession(null); setEditSessionData({ unit_name: "", car_id: "", car_name: "", session_start: "", timestamp: "" }); } }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>{selectedSession ? "Edit Session" : "Create Session"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Unit Name</Label>
              <Select value={editSessionData.unit_name} onValueChange={(v) => setEditSessionData({ ...editSessionData, unit_name: v })}>
                <SelectTrigger className="input-dark"><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  {patrolUnits.map((unit) => (
                    <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Car</Label>
              <Select value={editSessionData.car_id} onValueChange={(v) => {
                const car = cars.find(c => c.car_id === v);
                setEditSessionData({ ...editSessionData, car_id: v, car_name: car?.name || "" });
              }}>
                <SelectTrigger className="input-dark"><SelectValue placeholder="Select car" /></SelectTrigger>
                <SelectContent>
                  {cars.map((car) => (
                    <SelectItem key={car.car_id} value={car.car_id}>{car.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Session Start</Label>
              <Input
                type="datetime-local"
                value={editSessionData.session_start}
                onChange={(e) => setEditSessionData({ ...editSessionData, session_start: e.target.value })}
                className="input-dark"
              />
            </div>
            <div>
              <Label>Session End</Label>
              <Input
                type="datetime-local"
                value={editSessionData.timestamp}
                onChange={(e) => setEditSessionData({ ...editSessionData, timestamp: e.target.value })}
                className="input-dark"
              />
            </div>
            {editSessionData.session_start && editSessionData.timestamp && (
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded border border-emerald-200 dark:border-emerald-800/50 text-emerald-800 dark:text-emerald-300 text-sm font-semibold">
                <Timer className="w-3 h-3 inline mr-1" />
                Duration: {editDurationPreview !== null ? `${Math.floor(editDurationPreview / 60)}h ${editDurationPreview % 60}m` : "Invalid"}
                {editDurationPreview !== null && editDurationPreview <= 0 && <span className="text-red-400 ml-2">(End must be after start)</span>}
              </div>
            )}
            <Button
              onClick={selectedSession ? handleEditSession : handleCreateSession}
              className="w-full btn-primary"
              disabled={!editSessionData.unit_name || !editSessionData.car_id || !editSessionData.session_start || !editSessionData.timestamp || (editDurationPreview !== null && editDurationPreview <= 0)}
            >
              {selectedSession ? <><Pencil className="w-4 h-4 mr-2" />Save Changes</> : <><ClipboardList className="w-4 h-4 mr-2" />Create Session</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Session Dialog */}
      <Dialog open={showDeleteSessionDialog} onOpenChange={(open) => { setShowDeleteSessionDialog(open); if (!open) setSelectedSession(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="text-red-400">Delete Session</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedSession && (
              <div className="p-3 bg-slate-950 rounded border border-slate-800">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="font-mono text-xs">{selectedSession.car_name}</Badge>
                  <span className="font-semibold">{selectedSession.unit_name}</span>
                </div>
                <div className="text-sm text-slate-400">
                  {selectedSession.session_start && format(new Date(selectedSession.session_start), "M/d h:mma")} → {format(new Date(selectedSession.timestamp), "M/d h:mma")}
                </div>
                <div className="text-sm text-emerald-400 font-semibold mt-1">
                  {Math.floor(selectedSession.session_duration_minutes / 60)}h {selectedSession.session_duration_minutes % 60}m
                </div>
              </div>
            )}
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800/50">
              <p className="text-sm text-amber-800 dark:text-amber-200"><AlertTriangle className="w-4 h-4 inline mr-1" />This will permanently delete this session. It will be removed from all reports, patrol stats, and the member dashboard.</p>
            </div>
            <Button onClick={handleDeleteSession} className="w-full" variant="destructive">
              <Trash2 className="w-4 h-4 mr-2" />Delete Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cooldown Dialog (Admin) */}
      <Dialog open={showCooldownDialog} onOpenChange={(open) => { setShowCooldownDialog(open); if (!open) { setCooldownUnit(""); setCooldownDuration(15); setCooldownCarId("__all__"); } }}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="text-red-400">Apply Cooldown</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Unit</Label>
              <Select value={cooldownUnit} onValueChange={setCooldownUnit}>
                <SelectTrigger className="input-dark">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {patrolUnits.map((unit) => (
                    <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Apply To</Label>
              <Select value={cooldownCarId} onValueChange={setCooldownCarId}>
                <SelectTrigger className="input-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Cars</SelectItem>
                  {cars.map((car) => (
                    <SelectItem key={car.car_id} value={car.car_id}>{car.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={cooldownDuration.toString()} onValueChange={(v) => setCooldownDuration(parseInt(v))}>
                <SelectTrigger className="input-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COOLDOWN_DURATIONS.map(d => (
                    <SelectItem key={d} value={d.toString()}>{d} minutes</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleApplyCooldown} className="w-full" variant="destructive" disabled={!cooldownUnit}>
              <Ban className="w-4 h-4 mr-2" />Apply Cooldown
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
